'use client'

import React from 'react'
import { EditorContent, type Editor } from '@tiptap/react'
import { promoteHeaderRows, wrapTablesInScrollContainer } from './meeting-notes-table-scroll'
import '@kiao-voice/ui-kit/kiao-prose.css'
import './meeting-notes-document.css'

/**
 * The attributes that make an element A MEETING NOTE.
 *
 * Exported because in editable mode they are not set by this component at all —
 * ProseMirror owns the editable element, so they are handed to it through
 * `editorProps.attributes` in `use-notes-editor.ts`. One definition, so the
 * read-only share page and the editor cannot drift into two slightly different
 * documents. `kiao-prose` is the blog's typography contract, so a note reads
 * the way an article does; `meeting-notes-document.css` adds what only a note
 * has and hangs its editing affordances off `data-editable`. The E2E flows
 * select on the class and data attributes.
 */
export function meetingNotesDocumentAttributes(
  editable: boolean,
  className?: string
): Record<string, string> {
  return {
    class: ['mn-body', 'meeting-notes-document', 'kiao-prose', className].filter(Boolean).join(' '),
    'data-meeting-notes-document': '',
    'data-editable': String(editable),
    // A `contenteditable` element has no implicit ARIA role, so the editable
    // document has to name itself — it is the only way a screen reader (and
    // every `getByRole('textbox')` in the tests) can find the thing you type in.
    role: 'textbox',
    'aria-multiline': 'true',
    'aria-readonly': String(!editable),
    // The document is prose the model wrote, not the user's typing; red squiggles
    // under every proper noun in a meeting note are noise.
    spellcheck: 'false',
  }
}

export interface MeetingNotesDocumentBodyProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'children' | 'dangerouslySetInnerHTML'> {
  contentHtml: string
  editable: boolean
  /**
   * Required when `editable` — the Tiptap instance from `useNotesEditor`.
   * Built by the caller rather than here so the panel can reach the same editor
   * for seeking and autosave without this component growing those concerns.
   */
  editor?: Editor | null
}

/**
 * The canonical meeting-note document surface.
 *
 * Two modes that deliberately do NOT share an element:
 *
 *   - **Read-only** (public shares, and the template preview) inserts an
 *     immutable snapshot once. There is no editor, no ProseMirror, and nothing
 *     to hydrate — which is also why its tables have to be given the scroll
 *     container `TableView` builds for the editable copy, and their header row
 *     the `<thead>` the blog's stylesheet expects; see
 *     `meeting-notes-table-scroll.ts`.
 *   - **Editable** mounts `EditorContent`. ProseMirror owns that DOM — which is
 *     why the document attributes ride in via `editorProps` rather than being
 *     set here, and why nothing may write into it from the outside. (That rule
 *     is what moved search highlighting to decorations; see
 *     `find-highlight-plugin.ts`.)
 *
 * `EditorContent` adds a wrapper element of its own, so the class and data
 * attributes sit on the inner editable element in that mode. No stylesheet uses
 * a child combinator against `.mn-body`, so the extra level is inert.
 */
export const MeetingNotesDocumentBody = React.forwardRef<
  HTMLElement,
  MeetingNotesDocumentBodyProps
>(function MeetingNotesDocumentBody(
  { contentHtml, editable, editor, className, ...articleProps },
  ref
) {
  if (editable) {
    return <EditorContent editor={editor ?? null} className="mn-host" {...articleProps} />
  }

  const attributes = meetingNotesDocumentAttributes(false, className)
  return (
    <article
      {...articleProps}
      ref={ref}
      className={attributes.class}
      data-meeting-notes-document=""
      data-editable="false"
      // Explicitly NOT editable rather than merely absent: this element also
      // renders the public share snapshot, and the stylesheet keys the
      // interactive timecode affordance off the editable state.
      contentEditable={false}
      suppressContentEditableWarning
      // No `role="textbox"`: a snapshot is an ARTICLE to read, and announcing a
      // text box the reader cannot type in is worse than announcing nothing.
      // The editable copy declares the role in `meetingNotesDocumentAttributes`.
      aria-readonly
      // The snapshot as stored, plus what its tables need to read like the
      // editor's and the blog's: a scroll container and a `<thead>`. Display
      // only — nothing here is ever written back.
      dangerouslySetInnerHTML={{
        __html: wrapTablesInScrollContainer(promoteHeaderRows(contentHtml)),
      }}
    />
  )
})
